<template>
  <modal name="update-user" class="modal-inner modal-update-user" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="600" height="auto" :scrollable="true" :resizable="true" :adaptive="true" :reset="true" @before-close="resetForm">
    <span class="close-button" @click="hide('update-user')"><BaseIcon icon="times-circle" class="text-white"/></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span v-if="updateInfo">{{ updateInfo.username }}</span>
    </div>
    <div class="modal-body">
      <ValidationObserver ref="formUpdateUser" v-if="updateInfo != null">
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.firstname" type="text" :field_name="$t('user_modal.first_name')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.lastname" type="text" :field_name="$t('user_modal.last_name')" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.email" type="text" :field_name="$t('user_modal.email')" rules="required|email" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.phone" type="text" :field_name="$t('user_modal.phone')" rules="required" />
          </div>
        </div>

        <!-- SecurOne Status Section -->
        <div class="mx-2 border-b border-secondary-400 my-5" v-if="updateInfo.securOne && user.role === 'admin'"></div>
        <div class="flex flex-wrap bg-gray-50 p-2 rounded" v-if="updateInfo.securOne && user.role === 'admin'">
          <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">SecurOne Integration</div>
          <div class="w-full md:w-1/2 px-2 py-1">
            <span class="text-xs font-bold text-gray-500">Status:</span>
            <span :class="{
              'text-green-600': updateInfo.securOne.status === 'success',
              'text-yellow-600': updateInfo.securOne.status === 'pending',
              'text-red-600': updateInfo.securOne.status === 'failed'
            }" class="ml-2 font-semibold uppercase text-xs">
              {{ updateInfo.securOne.status }}
            </span>
          </div>
          <div class="w-full md:w-1/2 px-2 py-1" v-if="updateInfo.securOne.securOneId">
            <span class="text-xs font-bold text-gray-500">ID:</span>
            <span class="ml-2 text-xs font-mono">{{ updateInfo.securOne.securOneId }}</span>
          </div>
          <div class="w-full px-2 py-1" v-if="updateInfo.securOne.lastSyncAt">
            <span class="text-xs font-bold text-gray-500">Last Sync:</span>
            <span class="ml-2 text-xs">{{ new Date(updateInfo.securOne.lastSyncAt).toLocaleString() }}</span>
          </div>
          
          <!-- Email Status -->
          <div class="w-full px-2 py-1 mt-1 border-t border-gray-200 pt-1">
             <span class="text-xs font-bold text-gray-500">Invitation Email:</span>
             <span v-if="updateInfo.securOne.emailSent === true" class="ml-2 text-xs font-bold text-green-600">
               SENT
             </span>
             <span v-else-if="updateInfo.securOne.emailSent === false" class="ml-2 text-xs font-bold text-red-600">
               FAILED
             </span>
             <span v-else class="ml-2 text-xs text-gray-400">
               Unknown
             </span>
             <div v-if="updateInfo.securOne.emailSent === false && updateInfo.securOne.emailError" class="text-xs text-red-500 mt-1">
               Error: {{ updateInfo.securOne.emailError }}
             </div>
          </div>

          <div class="w-full px-2 py-1 mt-2 bg-red-100 border border-red-200 rounded p-2" v-if="updateInfo.securOne.status === 'failed' && updateInfo.securOne.lastError">
            <div class="text-xs font-bold text-red-700">Error Details:</div>
            <div class="text-xs text-red-600 mt-1">{{ updateInfo.securOne.lastError.message }}</div>
            <div class="text-xs text-red-500 mt-1 font-mono" v-if="updateInfo.securOne.lastError.code">Code: {{ updateInfo.securOne.lastError.code }}</div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseInput v-model="updateInfo.username" type="text" :field_name="$t('user_modal.username')" :rules="/^(securone_basic|basic_user)$/.test(updateInfo.role) ? 'required' : 'required|alpha_dash'" disabled readonly />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2">
            <BaseSelect v-model="updateInfo.role" :options="filtered_roles" nameSelector="name" optionSelector="id" :field_name="$t('user_modal.role')" rules="required" @change="changeUserRole()" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'admin'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">Åtkomst till domän<b class="text-red-500 ml-1">*</b></div>
            <div class="flex flex-col space-y-2">
              <label class="inline-flex items-center">
                <input v-model="updateInfo.accessCountries" value="se" type="checkbox" class="form-checkbox h-4 w-4" />
                <span class="ml-2 text-sm">Sverige (.se)</span>
              </label>
              <label class="inline-flex items-center">
                <input v-model="updateInfo.accessCountries" value="no" type="checkbox" class="form-checkbox h-4 w-4" />
                <span class="ml-2 text-sm">Norge (.no)</span>
              </label>
            </div>
          </div>
        </div>

        <div class="mx-2 border-b border-secondary-400 my-5" v-if="updateInfo.role != 'admin'"></div>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/3 px-2 py-2" v-if="/^reseller|^superuser|^user|^securdesktop|^securone_basic|^basic_user/.test(updateInfo.role) && user.role == 'admin'">
            <BaseSelect v-model="updateInfo.reseller_id" :options="resellers" nameSelector="name" optionSelector="_id" field_name="Återförsäljare" :rules="/^reseller/.test(updateInfo.role) ? 'required' : ''" @change="changeReseller()" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="/^reseller/.test(updateInfo.role)">
            <BaseSelect v-model="updateInfo.reseller_id" :options="resellers" nameSelector="name" optionSelector="_id" field_name="Återförsäljare" :rules="/^reseller/.test(updateInfo.role) ? 'required' : ''" />
          </div>
          <div class="w-full md:w-1/3 flex flex-col px-2 py-2" v-if="updateInfo.role == 'superuser' || updateInfo.role == 'user' || updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.customer") }}<b class="text-red-500 ml-1">*</b></div>
            <div class="w-full">
              <div class="mb-2">
                <input type="text" v-model.trim="customerSearch" placeholder="Sök" class="bg-gray-100 rounded w-full text-xs text-gray-900 focus:outline-none border-b border-gray-300 transition duration-500 px-2 py-2" />
              </div>
              <div class="max-h-80 overflow-y-scroll border border-gray-200 rounded px-2 py-2">
                <div v-if="filteredCustomer.length == 0" class="w-full text-xs italic text-gray-500 py-2">{{ $t("user_modal.no_cutomer_found") }}</div>
                <div class="px-2 transition ease-in-out duration-200" v-for="c in filteredCustomer" :key="c._id" v-bind:class="{ 'bg-gray-100': updateInfo.customers_id.indexOf(c.customer_id) > -1 || updateInfo.customers_id.indexOf(String(c._id)) > -1 }">
                  <label class="inline-flex items-center mt-2">
                    <input
                      v-if="updateInfo.role == 'superuser' || updateInfo.role == 'user'"
                      v-model="updateInfo.customers_id"
                      :value="c.customer_id"
                      type="checkbox"
                      class="form-checkbox h-4 w-4"
                      @change="
                        filterUnitByCustomer();
                        pruneUnits();
                      "
                    />
                    <input
                      v-if="updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'"
                      v-model="updateInfo.customers_id[0]"
                      :value="c.customer_id"
                      type="radio"
                      class="form-checkbox h-4 w-4"
                      @change="
                        filterUnitByCustomer();
                        updateInfo.units_id = [];
                      "
                    />
                    <span class="ml-2 text-sm font-sans font-semibold">{{ c.name }}</span>
                    <!-- SEC-334: a customer still stored on this user that the chosen reseller no
                         longer sells. Without this it is invisible in the form and survives every
                         save, so nobody can take it away. Untick it and save to remove it. -->
                    <span v-if="isStrandedCustomer(c)" class="ml-2 text-xs italic text-red-600">{{ $t("user_modal.customer_not_sold_by_reseller") }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/3 flex flex-col px-2 py-2" v-if="updateInfo.role == 'user' || updateInfo.role == 'securdesktop' || updateInfo.role == 'securone_basic' || updateInfo.role == 'basic_user'">
            <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.operation") }}<b class="text-red-500 ml-1">*</b></div>
            <div class="w-full">
              <div class="mb-2">
                <input type="text" v-model.trim="unitSearch" placeholder="Sök" class="bg-gray-100 rounded w-full text-xs text-gray-900 focus:outline-none border-b border-gray-300 transition duration-500 px-2 py-2" />
              </div>
              <div class="mb-2 px-2" v-if="filteredUnit.length > 0">
                <label class="inline-flex items-center">
                  <input type="checkbox" class="form-checkbox h-4 w-4" :checked="allListedUnitsSelected" @change="toggleAllUnits()" />
                  <span class="ml-2 text-sm font-sans font-semibold">{{ allListedUnitsSelected ? $t("user_modal.deselect_all") : $t("user_modal.select_all") }}</span>
                </label>
              </div>
              <div class="max-h-80 overflow-y-scroll border border-gray-200 rounded px-2 py-2">
                <div v-if="filteredUnit.length == 0" class="w-full text-xs italic text-gray-500 py-2">{{ $t("user_modal.no_department_found") }}</div>
                <div v-for="g in groupedFilteredUnits" :key="g.customer_id">
                  <div class="text-xs font-bold text-gray-500 uppercase mt-3 mb-1">{{ g.customer_name }}</div>
                  <div class="px-2 transition ease-in-out duration-200" v-for="u in g.units" :key="u._id" v-bind:class="{ 'bg-gray-100': updateInfo.units_id.indexOf(u.unit_id) > -1 }">
                    <label class="inline-flex items-center mt-2">
                      <input v-model="updateInfo.units_id" :value="u.unit_id" type="checkbox" class="form-checkbox h-4 w-4" /><span class="ml-2 text-sm font-sans font-semibold">{{ u.name }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'alarm_center'">
            <BaseSelect v-model="updateInfo.alarm_center_id" :options="alarm_centers" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.alarm_center')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'seller'">
            <BaseSelect v-model="updateInfo.seller_id" :options="sellers" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.seller')" rules="required" />
          </div>
          <div class="w-full md:w-1/3 px-2 py-2" v-if="updateInfo.role == 'tech'">
            <BaseSelect v-model="updateInfo.tech_id" :options="technicians" nameSelector="name" optionSelector="_id" :field_name="$t('user_role.tech')" rules="required" />
          </div>
        </div>
        <!-- SEC-329: reset this user's password. Admin only, and never on the
             admin's own record — that would lock them out of the session they
             are working in. -->
        <div class="mx-2 border-b border-secondary-400 my-5" v-if="canResetPassword"></div>
        <div class="flex flex-wrap bg-gray-50 p-2 rounded" v-if="canResetPassword">
          <div class="w-full text-gray-600 text-sm font-bold pb-2 font-serif">{{ $t("user_modal.password_reset_title") }}</div>

          <div class="w-full px-2 py-1" v-if="!passwordResetConfirming">
            <button type="button" class="btn-blue-outline px-3 py-2" @click="startPasswordReset()">{{ $t("user_modal.reset_password") }}<BaseIcon icon="key" class="ml-1" /></button>
            <div class="text-xs italic text-gray-500 mt-2">{{ $t("user_modal.reset_password_hint") }}</div>
            <div class="text-xs text-gray-500 mt-1" v-if="updateInfo.passwordResetAt">
              {{ $t("user_modal.last_reset", { by: updateInfo.passwordResetBy, at: formatResetTime(updateInfo.passwordResetAt) }) }}
            </div>
          </div>

          <div class="w-full px-2 py-1" v-else>
            <div class="text-sm text-gray-700">{{ $t("user_modal.reset_password_confirm", { email: updateInfo.email }) }}</div>
            <div class="mt-2">
              <button type="button" class="btn-green px-3 py-2 mr-2" :disabled="passwordResetSending" @click="confirmPasswordReset()">{{ $t("user_modal.reset_password_send") }}</button>
              <button type="button" class="btn-red-outline px-3 py-2" :disabled="passwordResetSending" @click="cancelPasswordReset()">{{ $t("user_actions.cancel") }}</button>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide('update-user')">{{ $t("user_actions.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button class="btn-green px-3 py-2 float-right" @click="updateUser()">{{ $t("user_actions.save") }}<BaseIcon icon="save" class="ml-1" /></button>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 800;

export default {
  props: {
    userInfo: {
      required: true,
      default: null,
    },
    roles: {
      required: true,
    },
    allCustomers: {
      required: true,
    },
    allUnits: {
      required: true,
    },
    alarm_centers: {
      required: true,
    },
    resellers: {
      required: true,
    },
    sellers: {
      required: true,
    },
    technicians: {
      required: true,
    },
  },
  data() {
    return {
      updateInfo: null,
      // SEC-329: the password reset asks once before it sends
      passwordResetConfirming: false,
      passwordResetSending: false,
      customerSearch: "",
      unitSearch: "",
      filtered_roles: [],
      customers: [],
      units: [],
      customer_units: [],
    };
  },
  watch: {
    // SEC-329: a different record means the pending confirm no longer applies.
    // A separate watcher so the existing userInfo watcher stays as it is.
    updateInfo() {
      this.passwordResetConfirming = false;
      this.passwordResetSending = false;
    },
    userInfo(newVal) {
      // eslint-disable-line
      this.updateInfo = _.cloneDeep(newVal);

      this.customerSearch = "";
      this.unitSearch = "";
      this.customer_units = [];
      // SecurOne basic users store customer and units as ids (the app's convention); the pickers work in codes
      if (/^(basic_user|securone_basic)$/.test(this.updateInfo.role)) {
        this.updateInfo.customers_id = (this.updateInfo.customers_id || []).map((v) => { const c = this.customers.find((x) => String(x._id) === String(v)); return c ? c.customer_id : v; });
        this.updateInfo.units_id = (this.updateInfo.units_id || []).map((v) => { const u = this.units.find((x) => String(x._id) === String(v)); return u ? u.unit_id : v; });
      }
      if (this.updateInfo.role == "superuser" || this.updateInfo.role == "user" || this.updateInfo.role == "securdesktop" || this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") {
        if (this.updateInfo.reseller_id != "" && this.user.role == "admin") this.getResellerCustomers();
        this.filterUnitByCustomer();
      }
    },
    allCustomers(newVal) {
      this.customers = _.cloneDeep(newVal);
    },
    allUnits(newVal) {
      this.units = _.cloneDeep(newVal);
    },
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    // SEC-329: only an admin resets passwords, and not on their own record.
    // Usernames are unique, so they identify the record without an id.
    canResetPassword() {
      if (!this.updateInfo) return false;
      if (this.user.role !== "admin") return false;

      return this.updateInfo.username !== this.user.username;
    },
    // SEC-334: what the checkbox list may draw. The customers this reseller sells, plus any
    // still stored on the user that it no longer sells. That second group exists whenever a
    // customer is moved to another seller after being given to a user. It is filtered out of
    // this.customers, so without this it never renders, yet it is still saved with every edit
    // and keeps the user's access to another brand's customer.
    selectableCustomers() {
      const vm = this;
      const stranded = _.filter(this.allCustomers, function(o) {
        return vm.updateInfo.customers_id.indexOf(o.customer_id) > -1 && !_.some(vm.customers, { customer_id: o.customer_id });
      });
      return this.customers.concat(stranded);
    },
    filteredCustomer() {
      var vm = this;
      let c = _.filter(this.selectableCustomers, function(o) {
        return o.name.toLowerCase().includes(vm.customerSearch.toLowerCase()) || vm.updateInfo.customers_id.indexOf(o.customer_id) > -1;
      });
      return c;
    },
    filteredUnit() {
      var vm = this;
      let u = _.filter(this.customer_units, function(o) {
        return o.name.toLowerCase().includes(vm.unitSearch.toLowerCase()) || vm.updateInfo.units_id.indexOf(o.unit_id) > -1;
      });
      return u;
    },
    // Units (search-filtered) grouped under their parent customer so a
    // multi-customer 'user' sees which verksamhet belongs to which customer.
    groupedFilteredUnits() {
      let byParent = _.groupBy(this.filteredUnit, (u) => String(u.parent_id));
      return _.map(byParent, (units, parentId) => {
        let c = _.find(this.customers, (x) => String(x._id) == parentId);
        return { customer_id: parentId, customer_name: c ? c.name : "", units };
      });
    },
    // True when every verksamhet currently listed is ticked. Scoped to the
    // listed units rather than to every unit of the kund, so the control can
    // never tick a verksamhet the search has filtered away.
    allListedUnitsSelected() {
      if (this.filteredUnit.length == 0) return false;
      let listedIds = _.map(this.filteredUnit, "unit_id");
      return _.every(listedIds, (id) => this.updateInfo.units_id.indexOf(id) > -1);
    },
  },
  methods: {
    // SEC-329: ask once before sending, so the button cannot be hit by accident.
    startPasswordReset() {
      this.passwordResetConfirming = true;
    },

    cancelPasswordReset() {
      this.passwordResetConfirming = false;
    },

    // Send the reset. Everything about the credential is decided on the
    // server; the reply carries nothing that could be used to log in.
    async confirmPasswordReset() {
      this.passwordResetSending = true;

      try {
        await this.axios.post(`${process.env.VUE_APP_SERVER_URL}/user/${this.updateInfo._id}/reset_password`);

        this.passwordResetConfirming = false;
        this.handleSuccess(this.$t("user_modal.reset_password_sent", { email: this.updateInfo.email }));

        // show it on the record right away, without reopening the modal
        this.$set(this.updateInfo, "passwordResetBy", this.user.username);
        this.$set(this.updateInfo, "passwordResetAt", new Date().toISOString());
      }
      catch (error) {
        // the server explains what went wrong (no email on the user, mail
        // could not be sent); fall back to the generic message
        let message = this.$t("problem_occured");
        if (error.response && error.response.data && error.response.data.message) {
          message = error.response.data.message;
        }
        this.handleError(error, message);
      }

      this.passwordResetSending = false;
    },

    formatResetTime(value) {
      if (!value) return "";
      return this.moment(value).format("YYYY-MM-DD, HH:mm");
    },

    async updateUser() {
      this.$refs.formUpdateUser.validate().then(async (success) => {
        if (!success) {
          return;
        }
        if (this.updateInfo.role == "user" && this.updateInfo.units_id.length == 0) {
          this.handleError(null, "Ingen verksamhet vald");
          return;
        }
        if ((this.updateInfo.role == "securdesktop" || this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") && this.updateInfo.units_id.length == 0) {
          this.handleError(null, "Ingen verksamhet vald");
          return;
        }
        if ((this.updateInfo.role == "securone_basic" || this.updateInfo.role == "basic_user") && this.updateInfo.customers_id.filter(Boolean).length != 1) {
          this.handleError(null, "En SecurOne-användare tillhör exakt en kund");
          return;
        }
        if (this.updateInfo.role == "securone_basic" && this.updateInfo.customers_id.filter(Boolean).length != 1) {
          this.handleError(null, "En SecurOne-användare tillhör exakt en kund");
          return;
        }
        if (this.updateInfo.role == "superuser" && this.updateInfo.customers_id.length == 0) {
          this.handleError(null, "Ingen kund vald");
          return;
        }
        
        if (this.updateInfo.role == 'admin' && (!this.updateInfo.accessCountries || this.updateInfo.accessCountries.length == 0)) {
          this.handleError(null, "Välj minst en domän (Sverige eller Norge)");
          return;
        }

        if (this.updateInfo.role != "superuser" && this.updateInfo.role != "user" && this.updateInfo.role != "securdesktop" && this.updateInfo.role != "securone_basic" && this.updateInfo.role != "basic_user") this.updateInfo.customers_id = [];
        if (this.updateInfo.role != "user" && this.updateInfo.role != "securdesktop" && this.updateInfo.role != "securone_basic" && this.updateInfo.role != "basic_user") this.updateInfo.units_id = [];
        if (this.updateInfo.role != "alarm_center") this.updateInfo.alarm_center_id = "";
        if (this.updateInfo.role != "reseller" && this.updateInfo.role != "superuser" && this.updateInfo.role != "user" && this.updateInfo.role != "alarm_center" && this.updateInfo.role != "securdesktop") this.updateInfo.reseller_id = "";
        if (this.updateInfo.role != "seller") this.updateInfo.seller_id = "";
        if (this.updateInfo.role != "tech") this.updateInfo.tech_id = "";

        let data = {
          firstname: this.updateInfo.firstname,
          lastname: this.updateInfo.lastname,
          username: this.updateInfo.username.toLowerCase(),
          email: this.updateInfo.email,
          phone: this.updateInfo.phone,
          role: this.updateInfo.role,
          // customer_id: this.updateInfo.customer_id,
          customers_id: this.updateInfo.customers_id,
          units_id: this.updateInfo.units_id,
          alarm_center_id: this.updateInfo.alarm_center_id,
          reseller_id: this.updateInfo.reseller_id,
          seller_id: this.updateInfo.seller_id,
          tech_id: this.updateInfo.tech_id,
          accessCountries: this.updateInfo.role == 'admin' ? this.updateInfo.accessCountries : undefined,
        };
        try {
          // eslint-disable-next-line
          let response = await this.axios.put(`${process.env.VUE_APP_SERVER_URL}/user/${this.updateInfo._id}`, data);
          this.$emit("updateUser");
          this.$modal.hide("update-user");
        } catch (error) {
          let error_message = "Något problem uppstod";
          if (error.response && error.response.status == 400) error_message = "Otillräckliga behörigheter";
          this.handleError(error, error_message);
        }
      });
    },

    filterUnitByCustomer() {
      if (this.updateInfo.customers_id.length == 0) {
        this.customer_units = [];
        return;
      }

      // Union of units across ALL selected customers (role 'user' may span
      // multiple customers; 'securdesktop' resolves to a single customer).
      let parentIds = _.map(
        _.filter(this.customers, (c) => this.updateInfo.customers_id.indexOf(c.customer_id) > -1),
        (c) => String(c._id)
      );
      this.customer_units = _.filter(this.units, (u) => parentIds.indexOf(String(u.parent_id)) > -1);
    },

    // Drop any selected unit that no longer belongs to a selected customer
    // (only call on customer-checkbox change, never in the open/watch path).
    pruneUnits() {
      let validUnitIds = _.map(this.customer_units, "unit_id");
      this.updateInfo.units_id = _.filter(this.updateInfo.units_id, (id) => validUnitIds.indexOf(id) > -1);
    },

    // "Select all" control: tick every verksamhet currently listed, or untick
    // them when they were all ticked already. With no search term the list is
    // the whole kund, so this still selects or clears everything. With a search
    // term it only touches what the list shows — a verksamhet filtered away is
    // never granted by accident.
    toggleAllUnits() {
      let listedIds = _.map(this.filteredUnit, "unit_id");

      if (this.allListedUnitsSelected) {
        this.updateInfo.units_id = _.difference(this.updateInfo.units_id, listedIds);
      } else {
        this.updateInfo.units_id = _.union(this.updateInfo.units_id, listedIds);
      }
    },

    changeUserRole() {
      this.changeReseller();
    },

    changeReseller() {
      if (!/^reseller|^superuser|^user|^securdesktop/.test(this.updateInfo.role)) {
        if (this.updateInfo.reseller_id != "") {
          this.getResellerCustomers();
        } else {
          this.customers = _.cloneDeep(this.allCustomers);
          this.units = _.cloneDeep(this.allUnits);
        }

        this.updateInfo.customers_id = [];
        this.updateInfo.units_id = [];
        this.customer_units = [];
      }
    },

    // SEC-334: stored on this user, but the chosen reseller does not sell it. Marked in the list
    // so an admin can see it and untick it; nothing is removed without that click.
    isStrandedCustomer(customer) {
      return !_.some(this.customers, { customer_id: customer.customer_id });
    },

    getResellerCustomers() {
      var vm = this;

      this.units = _.filter(this.allUnits, { seller_id: this.updateInfo.reseller_id });
      let customerIDs = _.uniq(_.map(this.units, "parent_id"));
      this.customers = _.filter(this.allCustomers, function(o) {
        return customerIDs.indexOf(o._id) > -1 || o.seller == vm.updateInfo.reseller_id;
      });
    },

    filterRoles() {
      if (this.user.role !== "admin" && this.user.role !== "reseller") {
        this.filtered_roles = _.filter(this.roles, function(o) {
          return o.id == "superuser" || o.id == "user" || o.id == "securdesktop";
        });
      } 
      else if (this.user.role === "reseller") {
        this.filtered_roles = _.filter(this.roles, function(o) {
          return o.id == "superuser" || o.id == "user" || o.id == "alarm_center";
        });
      } 
      else {
        this.filtered_roles = this.roles;
      }
    },

    resetForm() {
      this.$refs.formUpdateUser.reset();
      this.customers = _.cloneDeep(this.allCustomers);
      this.units = _.cloneDeep(this.allUnits);
      this.filterUnitByCustomer();
    },

    hide(id) {
      this.$modal.hide(id);
    },
  },

  created() {
    this.modalWidth = window.innerWidth < MODAL_WIDTH ? window.innerWidth : MODAL_WIDTH;
    this.filterRoles();
  },
};
</script>
